// Dependency-free responsive SVG trend chart — smooth area/line over daily
// values, y gridlines, sparse date ticks, and touch/pointer scrubbing.
// Shared by the owner Analytics page and both admin analytics surfaces;
// colors come from --tc-* custom properties themed per tree in trendChart.css.
// Same no-charting-library policy as the rest of the app.

import { useEffect, useId, useRef, useState } from 'react'

import './trendChart.css'

interface TrendPoint {
  date: string
  value: number
}

interface TrendChartProps {
  data: TrendPoint[]
  /** Tooltip/aria unit, e.g. "stamps". */
  unit: string
}

const HEIGHT = 220
const PAD_TOP = 14
const PAD_BOTTOM = 26
const PAD_LEFT = 38
const PAD_RIGHT = 12

export function TrendChart({ data, unit }: TrendChartProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [hover, setHover] = useState<number | null>(null)
  const gradientId = useId()

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setWidth(el.clientWidth))
    ro.observe(el)
    setWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [])

  if (data.length === 0) return <div ref={wrapRef} className="tc-chart" />

  const max = niceMax(Math.max(...data.map((d) => d.value)))
  const innerW = Math.max(0, width - PAD_LEFT - PAD_RIGHT)
  const innerH = HEIGHT - PAD_TOP - PAD_BOTTOM
  const baseY = PAD_TOP + innerH
  const stepX = data.length > 1 ? innerW / (data.length - 1) : 0
  const points = data.map((d, i) => ({
    x: PAD_LEFT + (data.length > 1 ? i * stepX : innerW / 2),
    y: baseY - (d.value / max) * innerH,
  }))

  const linePath = smoothPath(points, PAD_TOP, baseY)
  const areaPath = linePath
    ? `${linePath} L ${points[points.length - 1]!.x} ${baseY} L ${points[0]!.x} ${baseY} Z`
    : ''

  // ~5 evenly spaced date ticks, always including the first and last day.
  const tickStep = Math.max(1, Math.round((data.length - 1) / 4))
  const tickIdx = new Set<number>([data.length - 1])
  for (let i = 0; i < data.length - 1; i += tickStep) tickIdx.add(i)
  // Drop a second-to-last tick that would crowd the final label.
  for (const i of tickIdx) {
    if (i !== data.length - 1 && (data.length - 1 - i) * stepX < 34) {
      tickIdx.delete(i)
    }
  }

  const gridValues = [max, max / 2]
  const hovered = hover != null ? data[hover] : null
  const hoveredPt = hover != null ? points[hover] : null

  function scrub(e: React.PointerEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    if (stepX === 0) {
      setHover(0)
      return
    }
    const idx = Math.round((x - PAD_LEFT) / stepX)
    setHover(Math.max(0, Math.min(data.length - 1, idx)))
  }

  return (
    <div ref={wrapRef} className="tc-chart">
      {width > 0 && (
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={`Daily ${unit} from ${data[0]!.date} to ${data[data.length - 1]!.date}`}
          /* pan-y: vertical page scroll keeps working when a touch starts on
             the chart; horizontal drags scrub. No tappable controls inside. */
          style={{ touchAction: 'pan-y', display: 'block' }}
          onPointerMove={scrub}
          onPointerDown={scrub}
          onPointerLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" className="tc-gradient-top" />
              <stop offset="100%" className="tc-gradient-bottom" />
            </linearGradient>
          </defs>

          {gridValues.map((v) => {
            const y = baseY - (v / max) * innerH
            return (
              <g key={v}>
                <line
                  x1={PAD_LEFT}
                  x2={width - PAD_RIGHT}
                  y1={y}
                  y2={y}
                  className="tc-grid"
                />
                <text x={PAD_LEFT - 8} y={y + 3.5} className="tc-ylabel">
                  {abbreviate(v)}
                </text>
              </g>
            )
          })}
          <line
            x1={PAD_LEFT}
            x2={width - PAD_RIGHT}
            y1={baseY}
            y2={baseY}
            className="tc-axis"
          />
          <text x={PAD_LEFT - 8} y={baseY + 3.5} className="tc-ylabel">
            0
          </text>

          {areaPath && <path d={areaPath} fill={`url(#${gradientId})`} />}
          {linePath && <path d={linePath} className="tc-line" />}

          {[...tickIdx].map((i) => (
            <text
              key={data[i]!.date}
              x={points[i]!.x}
              y={HEIGHT - 8}
              className="tc-xlabel"
              textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'}
            >
              {formatDay(data[i]!.date)}
            </text>
          ))}

          {hoveredPt && (
            <g>
              <line
                x1={hoveredPt.x}
                x2={hoveredPt.x}
                y1={PAD_TOP}
                y2={baseY}
                className="tc-cursor"
              />
              <circle cx={hoveredPt.x} cy={hoveredPt.y} r="4.5" className="tc-dot" />
            </g>
          )}
        </svg>
      )}

      <div className="tc-readout" aria-live="polite">
        {hovered ? (
          <>
            <span className="tc-readout-date">{formatDay(hovered.date)}</span>
            <span className="tc-readout-value">
              {hovered.value.toLocaleString()} {unit}
            </span>
          </>
        ) : (
          <span className="tc-readout-hint">
            Tap or hover on the chart to see a day's exact number
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * Catmull-Rom → cubic bezier for a gently smoothed line. Control-point y's
 * are clamped to the plot area so a spike can't make the curve dip below
 * the zero baseline or overshoot the top.
 */
function smoothPath(
  pts: Array<{ x: number; y: number }>,
  minY: number,
  maxY: number,
): string {
  if (pts.length === 0) return ''
  if (pts.length === 1) {
    return `M ${pts[0]!.x - 4} ${pts[0]!.y} L ${pts[0]!.x + 4} ${pts[0]!.y}`
  }
  const clamp = (y: number) => Math.max(minY, Math.min(maxY, y))
  let d = `M ${pts[0]!.x} ${pts[0]!.y}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!
    const p1 = pts[i]!
    const p2 = pts[i + 1]!
    const p3 = pts[i + 2] ?? p2
    const c1x = p1.x + (p2.x - p0.x) / 6
    const c1y = clamp(p1.y + (p2.y - p0.y) / 6)
    const c2x = p2.x - (p3.x - p1.x) / 6
    const c2y = clamp(p2.y - (p3.y - p1.y) / 6)
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`
  }
  return d
}

/** Round a data max up to a friendly axis ceiling (1/2/2.5/5 × 10^k). */
function niceMax(value: number): number {
  if (value <= 0) return 4
  const exp = Math.floor(Math.log10(value))
  const base = 10 ** exp
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (value <= m * base) return m * base
  }
  return 10 * base
}

function abbreviate(n: number): string {
  if (n >= 1000) {
    const k = n / 1000
    return `${Number.isInteger(k) ? k : k.toFixed(1)}k`
  }
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

/** "2026-05-27" → "May 27". PH style, locale-stable. */
function formatDay(iso: string): string {
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ]
  const [, m, d] = iso.split('-')
  const idx = Number(m) - 1
  if (!Number.isFinite(idx) || idx < 0 || idx > 11) return iso
  return `${months[idx]} ${Number(d)}`
}
